const CHAT_BASE_URL = "http://localhost:5000";
const CHAT_TENANT_ID = "1";
const CHAT_SCRIPT_SRC = `${CHAT_BASE_URL}/embed/chat.js?tenant_id=${CHAT_TENANT_ID}`;

const injectedUiSelectors = [
  "#chat-widget-container",
  "#chat-widget-button",
  "#chat-window",
  "#chaskiq-widget",
  ".chaskiq-messenger-bubble",
  "[id^='chat-js-']",
];

let scriptPromise;
let observer;
let lastFetchError;

function configureChatScript() {
  if (typeof window === "undefined") return;

  window.ChatAppConfig = {
    ...(window.ChatAppConfig || {}),
    tenant_id: CHAT_TENANT_ID,
    tenantId: CHAT_TENANT_ID,
    apiBaseUrl: CHAT_BASE_URL,
    baseUrl: CHAT_BASE_URL,
    autoMount: false,
    disableWidget: true,
    hideDefaultWidget: true,
  };
}

function suppressInjectedChatUi() {
  if (typeof document === "undefined") return;

  for (const selector of injectedUiSelectors) {
    document.querySelectorAll(selector).forEach((node) => {
      if (node.closest?.(".oliveup-live-chat-panel")) return;
      node.setAttribute("aria-hidden", "true");
      node.style.setProperty("display", "none", "important");
      node.style.setProperty("visibility", "hidden", "important");
      node.style.setProperty("opacity", "0", "important");
      node.style.setProperty("pointer-events", "none", "important");
    });
  }

  if (!observer && document.body) {
    observer = new MutationObserver(() => suppressInjectedChatUi());
    observer.observe(document.body, { childList: true, subtree: true });
  }
}

export function loadChatRouteScript() {
  if (typeof document === "undefined") return Promise.resolve(null);
  configureChatScript();
  suppressInjectedChatUi();

  const existing = document.querySelector(
    `script[src="${CHAT_SCRIPT_SRC}"], #oliveup-chat-route-script`,
  );
  if (existing) {
    return Promise.resolve(existing);
  }

  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.id = "oliveup-chat-route-script";
      script.src = CHAT_SCRIPT_SRC;
      script.async = true;
      script.crossOrigin = "anonymous";
      script.dataset.tenantId = CHAT_TENANT_ID;
      script.onload = () => {
        suppressInjectedChatUi();
        resolve(script);
      };
      script.onerror = () => reject(new Error("Unable to load chat route script"));
      document.head.appendChild(script);
    });
  }

  return scriptPromise;
}

function getChatApp() {
  if (typeof window === "undefined") return null;

  return (
    window.ChatApp ||
    window.chatApp ||
    window.ChatWidget ||
    window.chatWidget ||
    window.EmbeddedChat ||
    null
  );
}

function normalizeMessage(raw) {
  if (!raw) return null;

  if (typeof raw === "string") {
    return {
      id: `chat-${Date.now()}-${raw.length}`,
      from: "support",
      text: raw,
    };
  }

  const text =
    raw.text ||
    raw.message ||
    raw.content ||
    (typeof raw.body === "string" ? raw.body : null) ||
    raw.reply ||
    raw.answer;
  if (!text) return null;

  const author = raw.from || raw.role || raw.sender || raw.author_type || raw.author;
  const from =
    author === "visitor" || author === "user" || author === "customer"
      ? "visitor"
      : "support";

  return {
    id: String(raw.id || raw.message_id || raw.created_at || `${Date.now()}-${text}`),
    from,
    text,
  };
}

function normalizeMessages(result) {
  const payload = result?.messages || result?.data || result?.message || result;
  const list = Array.isArray(payload) ? payload : [payload];
  return list.map(normalizeMessage).filter(Boolean);
}

async function callChatAppMethod(names, ...args) {
  const app = getChatApp();
  if (!app) return null;

  for (const name of names) {
    const method = app[name] || app.api?.[name] || app.client?.[name];
    if (typeof method === "function") {
      return {
        called: true,
        value: method.call(app.api || app.client || app, ...args),
      };
    }
  }

  return null;
}

async function postToChatRoute(message, visitorMeta = {}) {
  const payload = {
    tenant_id: CHAT_TENANT_ID,
    tenantId: CHAT_TENANT_ID,
    message,
    text: message,
    content: message,
    ...(Object.keys(visitorMeta).length > 0 ? { visitor_meta: visitorMeta } : {}),
  };

  const paths = [
    "/api/chat/messages",
    "/api/chat/message",
    "/api/messages",
    "/api/message",
    "/chat/messages",
    "/chat/message",
  ];

  for (const path of paths) {
    const url = `${CHAT_BASE_URL}${path}?tenant_id=${CHAT_TENANT_ID}`;
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.status === 404 || response.status === 405) continue;
      if (!response.ok) {
        throw new Error(`Chat request failed with ${response.status}`);
      }

      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) return [];
      return normalizeMessages(await response.json());
    } catch (error) {
      lastFetchError = error;
    }
  }

      throw lastFetchError || new Error("Chat route unavailable");
}

export async function sendChatMessage(message, visitorMeta = {}) {
  const text = message.trim();
  if (!text) return [];

  try {
    await loadChatRouteScript();
  } catch (error) {
    lastFetchError = error;
  }

  const meta =
    visitorMeta && typeof visitorMeta === "object" && !Array.isArray(visitorMeta)
      ? visitorMeta
      : {};

  const payload = {
    tenant_id: CHAT_TENANT_ID,
    tenantId: CHAT_TENANT_ID,
    message: text,
    text,
    content: text,
    ...(Object.keys(meta).length > 0 ? { visitor_meta: meta } : {}),
  };

  try {
    const bridged = await callChatAppMethod(
      ["sendMessage", "send", "createMessage", "postMessage"],
      payload,
    );
    if (bridged?.called) return normalizeMessages(await bridged.value);
  } catch (error) {
    lastFetchError = error;
  }

  return postToChatRoute(text, meta);
}

export async function receiveChatMessages() {
  try {
    await loadChatRouteScript();
  } catch (error) {
    lastFetchError = error;
  }

  const bridged = await callChatAppMethod([
    "receiveMessage",
    "receiveMessages",
    "getMessages",
    "fetchMessages",
  ]);

  if (!bridged?.called) return [];
  return normalizeMessages(await bridged.value);
}

export function subscribeToChatMessages(onMessage) {
  if (typeof window === "undefined") return () => {};

  const cleanups = [];
  const handleMessage = (event) => {
    normalizeMessages(event.detail || event.data || event).forEach(onMessage);
  };

  [
    "chat:message",
    "chat:message:received",
    "chat:receive",
    "ChatApp:message",
    "ChatApp:message:received",
  ].forEach((eventName) => {
    window.addEventListener(eventName, handleMessage);
    cleanups.push(() => window.removeEventListener(eventName, handleMessage));
  });

  const app = getChatApp();
  if (app?.on) {
    ["message", "message:received", "receiveMessage"].forEach((eventName) => {
      app.on(eventName, handleMessage);
      cleanups.push(() => app.off?.(eventName, handleMessage));
    });
  }

  return () => cleanups.forEach((cleanup) => cleanup());
}

export { CHAT_BASE_URL, CHAT_SCRIPT_SRC, CHAT_TENANT_ID, suppressInjectedChatUi };
